import {
  buildStoryTeamBattleRole,
  STORY_BATTLE_ROLE_SOURCE,
} from "./storyHumanCharacterCards";
import { resolveActiveStoryCompanion } from "./storyCompanions";
import { resolveStoryBattleTeam } from "./storyTeamRules";
import whisperingForestSource from "../../components/mapScenes/whisperingForest/whispering-forest-source.json";

export const STORY_FOREST_PURGE_MISSION_ID = "act-1-mission-12";
export const STORY_RUNE_STONE_MISSION_ID = "act-1-mission-14";
export const STORY_FOREST_ENCOUNTER_MODE = "roamingCreatureClear";
export const STORY_FOREST_ENCOUNTER_SET_ID = "whispering-forest-corruption-1";
export const STORY_FOREST_COUNTER_LABEL = "Creatures Remaining";
export const STORY_FOREST_RUNE_STONE_INTERACTABLE_ID = "lord-runes-stone";
export const STORY_FOREST_RUNE_STONE_CARD_ID = "oqeOZ15U3aDR81CTmFig";
export const STORY_FOREST_RUNE_STONE_NAME = "Lord's Rune Stone";
export const STORY_FOREST_RUNE_STONE_IMAGE_KEY = "lords_rune_stone";
const STORY_FOREST_ENTRY_SPAWN_ID = "whispering-forest-entry";
const STORY_FOREST_RUNE_STONE_SPAWN_ID = "lord-runes-stone";
const STORY_FOREST_SAFE_EDGE_PADDING = 48;
const STORY_FOREST_WALKABLE_POINT_STEP = 16;

const STORY_FOREST_MAP_PIXEL_WIDTH =
  whisperingForestSource.width * whisperingForestSource.tilewidth;
const STORY_FOREST_MAP_PIXEL_HEIGHT =
  whisperingForestSource.height * whisperingForestSource.tileheight;

const getStoryForestObjectLayerByName = (name = "") =>
  whisperingForestSource.layers.find(
    (layer) => layer.type === "objectgroup" && layer.name === name,
  ) || null;

const STORY_FOREST_ANCHORS = new Map(
  (getStoryForestObjectLayerByName("Anchors")?.objects || [])
    .filter((object) => object?.name)
    .map((object) => [object.name, object]),
);

const buildStoryForestCollisionPolygons = () =>
  (getStoryForestObjectLayerByName("Collisions")?.objects || []).flatMap((object) => {
    if (Array.isArray(object.polygon) && object.polygon.length >= 3) {
      return [[
        ...object.polygon.map((point) => [
          Number(object.x || 0) + Number(point.x || 0),
          Number(object.y || 0) + Number(point.y || 0),
        ]),
      ]];
    }

    if (Number(object.width || 0) > 0 && Number(object.height || 0) > 0) {
      const x = Number(object.x || 0);
      const y = Number(object.y || 0);
      const width = Number(object.width || 0);
      const height = Number(object.height || 0);
      return [[
        [x, y],
        [x + width, y],
        [x + width, y + height],
        [x, y + height],
      ]];
    }

    return [];
  });

const STORY_FOREST_COLLISION_POLYGONS = buildStoryForestCollisionPolygons();

const RUNE_STONE_IMAGE_FIELDS = Object.freeze([
  "imageUrl",
  "image",
  "portrait",
  "thumbnail",
  "imageStart",
  "imageFin",
]);

const normalizeRuneStoneText = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const RUNE_STONE_NAME_KEYS = new Set([
  normalizeRuneStoneText(STORY_FOREST_RUNE_STONE_NAME),
  normalizeRuneStoneText("Lord Rune's Stone"),
  normalizeRuneStoneText("Lord Rune Stone"),
  normalizeRuneStoneText("Lord's Rune's Stone"),
]);

const toCatalogArray = (value = []) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value && typeof value === "object") return [value];
  return [];
};

const normalizeStoryForestPoint = (point = null) => {
  const x = Number(point?.x);
  const y = Number(point?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
};

const isInsideStoryForestPolygon = (x, y, polygon = []) => {
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const intersects =
      (yi > y) !== (yj > y) &&
      x < ((xj - xi) * (y - yi)) / ((yj - yi) || Number.EPSILON) + xi;

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
};

export const STORY_FOREST_MAP_BOUNDS = Object.freeze({
  minX: STORY_FOREST_SAFE_EDGE_PADDING,
  maxX: Math.max(
    STORY_FOREST_SAFE_EDGE_PADDING,
    STORY_FOREST_MAP_PIXEL_WIDTH - STORY_FOREST_SAFE_EDGE_PADDING,
  ),
  minY: STORY_FOREST_SAFE_EDGE_PADDING,
  maxY: Math.max(
    STORY_FOREST_SAFE_EDGE_PADDING,
    STORY_FOREST_MAP_PIXEL_HEIGHT - STORY_FOREST_SAFE_EDGE_PADDING,
  ),
});

export const isStoryForestPointBlocked = (point = null) => {
  const normalizedPoint = normalizeStoryForestPoint(point);
  if (!normalizedPoint) return true;

  if (
    normalizedPoint.x < STORY_FOREST_MAP_BOUNDS.minX ||
    normalizedPoint.x > STORY_FOREST_MAP_BOUNDS.maxX ||
    normalizedPoint.y < STORY_FOREST_MAP_BOUNDS.minY ||
    normalizedPoint.y > STORY_FOREST_MAP_BOUNDS.maxY
  ) {
    return true;
  }

  return STORY_FOREST_COLLISION_POLYGONS.some((polygon) =>
    isInsideStoryForestPolygon(normalizedPoint.x, normalizedPoint.y, polygon),
  );
};

export const resolveStoryForestWalkablePoint = (point = null, fallback = null) => {
  const normalizedPoint = normalizeStoryForestPoint(point);
  if (normalizedPoint && !isStoryForestPointBlocked(normalizedPoint)) {
    return normalizedPoint;
  }

  if (normalizedPoint) {
    for (
      let radius = STORY_FOREST_WALKABLE_POINT_STEP;
      radius <= Math.max(STORY_FOREST_MAP_PIXEL_WIDTH, STORY_FOREST_MAP_PIXEL_HEIGHT);
      radius += STORY_FOREST_WALKABLE_POINT_STEP
    ) {
      for (let dx = -radius; dx <= radius; dx += STORY_FOREST_WALKABLE_POINT_STEP) {
        const candidates = [
          { x: normalizedPoint.x + dx, y: normalizedPoint.y - radius },
          { x: normalizedPoint.x + dx, y: normalizedPoint.y + radius },
        ];

        for (const candidate of candidates) {
          if (!isStoryForestPointBlocked(candidate)) {
            return candidate;
          }
        }
      }

      for (
        let dy = -(radius - STORY_FOREST_WALKABLE_POINT_STEP);
        dy <= radius - STORY_FOREST_WALKABLE_POINT_STEP;
        dy += STORY_FOREST_WALKABLE_POINT_STEP
      ) {
        const candidates = [
          { x: normalizedPoint.x - radius, y: normalizedPoint.y + dy },
          { x: normalizedPoint.x + radius, y: normalizedPoint.y + dy },
        ];

        for (const candidate of candidates) {
          if (!isStoryForestPointBlocked(candidate)) {
            return candidate;
          }
        }
      }
    }
  }

  const normalizedFallback = normalizeStoryForestPoint(fallback);
  return normalizedFallback && !isStoryForestPointBlocked(normalizedFallback)
    ? normalizedFallback
    : null;
};

const resolveStoryForestAnchorPoint = (anchorName, fallback = null) =>
  resolveStoryForestWalkablePoint(STORY_FOREST_ANCHORS.get(anchorName), fallback)
  || normalizeStoryForestPoint(fallback);

export const getStoryRuneStoneCardImage = (card = null) => {
  if (!card) return "";
  const imageField = RUNE_STONE_IMAGE_FIELDS.find((field) =>
    Boolean(card?.[field]),
  );
  return imageField ? card[imageField] : "";
};

export const findStoryRuneStoneCard = (...catalogs) =>
  catalogs
    .flatMap(toCatalogArray)
    .find((card) => {
      const cardId = String(card?.id || card?.cardId || card?.sourceId || "").trim();
      if (cardId === STORY_FOREST_RUNE_STONE_CARD_ID) return true;
      return RUNE_STONE_NAME_KEYS.has(normalizeRuneStoneText(card?.name));
    }) || null;

export const isStoryRuneStoneInventoryItem = (
  item = {},
  { includeBattleCard = true } = {},
) => {
  const normalizedSourceId = normalizeRuneStoneText(
    item?.sourceId || item?.id || "",
  );
  const normalizedName = normalizeRuneStoneText(item?.name || "");
  const normalizedKind = String(item?.kind || "").trim().toLowerCase();

  if (
    normalizedKind === "storyitem" &&
    normalizedSourceId === normalizeRuneStoneText(STORY_FOREST_RUNE_STONE_INTERACTABLE_ID)
  ) {
    return true;
  }

  if (!includeBattleCard) return false;

  return (
    normalizedKind === "infinitycard" &&
    (
      normalizedSourceId === normalizeRuneStoneText(STORY_FOREST_RUNE_STONE_CARD_ID) ||
      normalizedName === normalizeRuneStoneText(STORY_FOREST_RUNE_STONE_NAME)
    )
  );
};

export const buildStoryRuneStoneInventoryItem = (
  card = null,
  { kind = "storyItem" } = {},
) => {
  const isBattleCard = String(kind || "").trim().toLowerCase() === "infinitycard";

  return {
    kind: isBattleCard ? "infinityCard" : "storyItem",
    sourceId: isBattleCard
      ? STORY_FOREST_RUNE_STONE_CARD_ID
      : STORY_FOREST_RUNE_STONE_INTERACTABLE_ID,
    id: isBattleCard
      ? STORY_FOREST_RUNE_STONE_CARD_ID
      : STORY_FOREST_RUNE_STONE_INTERACTABLE_ID,
    name: STORY_FOREST_RUNE_STONE_NAME,
    quantity: 1,
    imageUrl: getStoryRuneStoneCardImage(card),
    imageKey: STORY_FOREST_RUNE_STONE_IMAGE_KEY,
    stackable: false,
  };
};

export const STORY_FOREST_APPROACH_WORLD = Object.freeze({
  mapId: "voidCity",
  building: "",
  spawnId: "whispering-forest-portal",
  x: 1454,
  y: 1458,
});

const STORY_FOREST_WORLD_FALLBACK = Object.freeze({ x: 512, y: 880 });

export const STORY_FOREST_WORLD = Object.freeze({
  mapId: "whisperingForest",
  building: "",
  spawnId: STORY_FOREST_ENTRY_SPAWN_ID,
  ...resolveStoryForestAnchorPoint("forestEntrySpawn", STORY_FOREST_WORLD_FALLBACK),
});

export const STORY_FOREST_RUNE_STONE_REVEAL_POINT = Object.freeze({
  spawnId: STORY_FOREST_RUNE_STONE_SPAWN_ID,
  ...resolveStoryForestAnchorPoint("storyRuneStone", { x: 500, y: 300 }),
});

const STORY_FOREST_ENCOUNTER_SPECS = Object.freeze([
  {
    encounterId: "wf-pack-1",
    leadCreatureId: "geode",
    creatureIds: ["geode"],
    home: { x: 720, y: 520 },
    homeBounds: { width: 120, height: 96 },
    aggroRange: 124,
    giveUpRange: 224,
    touchRange: 34,
    roamSpeed: 0.34,
    chaseSpeed: 1.28,
    returnPoint: { x: 720, y: 556 },
  },
  {
    encounterId: "wf-pack-2",
    leadCreatureId: "sprout",
    creatureIds: ["sprout", "shroom"],
    home: { x: 816, y: 180 },
    homeBounds: { width: 148, height: 108 },
    aggroRange: 140,
    giveUpRange: 244,
    touchRange: 36,
    roamSpeed: 0.36,
    chaseSpeed: 1.34,
    returnPoint: { x: 816, y: 220 },
  },
  {
    encounterId: "wf-pack-3",
    leadCreatureId: "sprout",
    creatureIds: ["sprout"],
    home: { x: 860, y: 360 },
    homeBounds: { width: 124, height: 112 },
    aggroRange: 132,
    giveUpRange: 236,
    touchRange: 34,
    roamSpeed: 0.34,
    chaseSpeed: 1.26,
    returnPoint: { x: 860, y: 396 },
  },
  {
    encounterId: "wf-pack-4",
    leadCreatureId: "geode",
    creatureIds: ["geode", "shroom"],
    home: { x: 628, y: 444 },
    homeBounds: { width: 150, height: 118 },
    aggroRange: 148,
    giveUpRange: 258,
    touchRange: 36,
    roamSpeed: 0.33,
    chaseSpeed: 1.24,
    returnPoint: { x: 628, y: 478 },
  },
  {
    encounterId: "wf-pack-5",
    leadCreatureId: "sprout",
    creatureIds: ["sprout"],
    home: { x: 896, y: 556 },
    homeBounds: { width: 124, height: 100 },
    aggroRange: 138,
    giveUpRange: 242,
    touchRange: 34,
    roamSpeed: 0.38,
    chaseSpeed: 1.38,
    returnPoint: { x: 896, y: 590 },
  },
  {
    encounterId: "wf-pack-6",
    leadCreatureId: "shroom",
    creatureIds: ["shroom", "shroom"],
    home: { x: 680, y: 840 },
    homeBounds: { width: 128, height: 108 },
    aggroRange: 136,
    giveUpRange: 242,
    touchRange: 35,
    roamSpeed: 0.32,
    chaseSpeed: 1.2,
    returnPoint: { x: 680, y: 876 },
  },
  {
    encounterId: "wf-pack-7",
    leadCreatureId: "geode",
    creatureIds: ["geode", "sprout"],
    home: { x: 560, y: 760 },
    homeBounds: { width: 156, height: 122 },
    aggroRange: 148,
    giveUpRange: 258,
    touchRange: 36,
    roamSpeed: 0.35,
    chaseSpeed: 1.3,
    returnPoint: { x: 560, y: 796 },
  },
  {
    encounterId: "wf-pack-8",
    leadCreatureId: "shroom",
    creatureIds: ["shroom", "sprout", "geode"],
    home: { x: 824, y: 860 },
    homeBounds: { width: 164, height: 124 },
    aggroRange: 156,
    giveUpRange: 268,
    touchRange: 38,
    roamSpeed: 0.35,
    chaseSpeed: 1.36,
    returnPoint: { x: 824, y: 894 },
  },
]);

export const STORY_FOREST_ENCOUNTERS = Object.freeze(
  STORY_FOREST_ENCOUNTER_SPECS.map((encounter) => {
    const home = Object.freeze(resolveStoryForestWalkablePoint(
      encounter.home,
      STORY_FOREST_WORLD,
    ));
    const returnPoint = resolveStoryForestWalkablePoint(
      encounter.returnPoint,
      home,
    );

    return Object.freeze({
      encounterId: encounter.encounterId,
      leadCreatureId: encounter.leadCreatureId,
      creatureIds: Object.freeze([...encounter.creatureIds]),
      home,
      homeBounds: Object.freeze({ ...encounter.homeBounds }),
      aggroRange: encounter.aggroRange,
      giveUpRange: encounter.giveUpRange,
      touchRange: encounter.touchRange,
      roamSpeed: encounter.roamSpeed,
      chaseSpeed: encounter.chaseSpeed,
      returnTarget: Object.freeze({
        ...STORY_FOREST_WORLD,
        spawnId: encounter.encounterId,
        x: returnPoint.x,
        y: returnPoint.y,
      }),
    });
  }),
);

export const STORY_FOREST_ENCOUNTER_SET = Object.freeze({
  encounterSetId: STORY_FOREST_ENCOUNTER_SET_ID,
  missionId: STORY_FOREST_PURGE_MISSION_ID,
  counterLabel: STORY_FOREST_COUNTER_LABEL,
  revealInteractableId: STORY_FOREST_RUNE_STONE_INTERACTABLE_ID,
  revealPoint: STORY_FOREST_RUNE_STONE_REVEAL_POINT,
  world: STORY_FOREST_WORLD,
  encounters: STORY_FOREST_ENCOUNTERS,
});

const STORY_FOREST_ENCOUNTERS_BY_ID = new Map(
  STORY_FOREST_ENCOUNTERS.map((encounter) => [encounter.encounterId, encounter]),
);

const normalizeEncounterId = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeEncounterIdList = (value = []) =>
  [...new Set((Array.isArray(value) ? value : []).map(normalizeEncounterId).filter(Boolean))];

const normalizeInteractableIdList = (value = []) =>
  [...new Set((Array.isArray(value) ? value : []).map(normalizeEncounterId).filter(Boolean))];

export const getStoryForestEncounterSet = (encounterSetId = "") =>
  normalizeEncounterId(encounterSetId) === STORY_FOREST_ENCOUNTER_SET_ID
    ? STORY_FOREST_ENCOUNTER_SET
    : null;

export const getStoryForestEncounterById = (encounterId = "") =>
  STORY_FOREST_ENCOUNTERS_BY_ID.get(normalizeEncounterId(encounterId)) || null;

export const buildStoryForestEncounterReturnTarget = (encounterId = "") => {
  const encounter = getStoryForestEncounterById(encounterId);
  if (!encounter) return null;

  return {
    ...STORY_FOREST_WORLD,
    ...(encounter.returnTarget || {}),
    mapId: STORY_FOREST_WORLD.mapId,
    building: STORY_FOREST_WORLD.building,
  };
};

export const getStoryForestEncounterCount = (encounter = null) =>
  Array.isArray(encounter?.creatureIds) ? encounter.creatureIds.length : 0;

export const getStoryForestTotalCreatureCount = (
  encounterSet = STORY_FOREST_ENCOUNTER_SET,
) =>
  (encounterSet?.encounters || []).reduce(
    (total, encounter) => total + getStoryForestEncounterCount(encounter),
    0,
  );

const createDefaultForestMissionProgress = () => ({
  defeatedEncounterIds: [],
  defeatedCreatureCount: 0,
  totalCreatureCount: getStoryForestTotalCreatureCount(),
  revealedItemIds: [],
  lastTriggeredEncounterId: null,
});

export const normalizeStoryForestMissionProgress = (
  rawProgress = null,
  encounterSet = STORY_FOREST_ENCOUNTER_SET,
) => {
  const totalCreatureCount = Math.max(
    0,
    Number(rawProgress?.totalCreatureCount || getStoryForestTotalCreatureCount(encounterSet)),
  );
  const defeatedEncounterIds = normalizeEncounterIdList(
    rawProgress?.defeatedEncounterIds,
  ).filter((encounterId) => STORY_FOREST_ENCOUNTERS_BY_ID.has(encounterId));
  const defeatedCreatureCount = Math.max(
    0,
    Math.min(totalCreatureCount, Math.floor(Number(rawProgress?.defeatedCreatureCount || 0))),
  );
  const revealedItemIds = normalizeInteractableIdList(rawProgress?.revealedItemIds);

  return {
    defeatedEncounterIds,
    defeatedCreatureCount,
    totalCreatureCount,
    revealedItemIds,
    lastTriggeredEncounterId: normalizeEncounterId(rawProgress?.lastTriggeredEncounterId) || null,
  };
};

export const resolveStoryForestMissionProgress = (
  storyState = null,
  missionId = STORY_FOREST_PURGE_MISSION_ID,
  encounterSet = STORY_FOREST_ENCOUNTER_SET,
) => {
  const progressMap = storyState?.storyMissionEncounterProgress || {};
  const normalized = normalizeStoryForestMissionProgress(
    progressMap?.[missionId] || createDefaultForestMissionProgress(),
    encounterSet,
  );
  const totalCreatureCount = Math.max(
    normalized.totalCreatureCount,
    getStoryForestTotalCreatureCount(encounterSet),
  );
  const creaturesRemaining = Math.max(
    0,
    totalCreatureCount - normalized.defeatedCreatureCount,
  );
  const runeStoneRevealed =
    creaturesRemaining <= 0 ||
    normalized.revealedItemIds.includes(STORY_FOREST_RUNE_STONE_INTERACTABLE_ID);

  return {
    ...normalized,
    totalCreatureCount,
    creaturesRemaining,
    runeStoneRevealed,
  };
};

export const buildStoryForestVictoryProgress = ({
  storyState = null,
  missionId = STORY_FOREST_PURGE_MISSION_ID,
  encounterId = "",
  encounterSet = STORY_FOREST_ENCOUNTER_SET,
} = {}) => {
  const encounter = getStoryForestEncounterById(encounterId);
  const currentProgress = resolveStoryForestMissionProgress(
    storyState,
    missionId,
    encounterSet,
  );
  if (!encounter || currentProgress.defeatedEncounterIds.includes(encounter.encounterId)) {
    return currentProgress;
  }

  const defeatedEncounterIds = [
    ...currentProgress.defeatedEncounterIds,
    encounter.encounterId,
  ];
  const defeatedCreatureCount = Math.max(
    0,
    Math.min(
      currentProgress.totalCreatureCount,
      currentProgress.defeatedCreatureCount + getStoryForestEncounterCount(encounter),
    ),
  );
  const runeStoneRevealed =
    defeatedCreatureCount >= currentProgress.totalCreatureCount;

  return {
    ...currentProgress,
    defeatedEncounterIds,
    defeatedCreatureCount,
    revealedItemIds: runeStoneRevealed
      ? normalizeInteractableIdList([
          ...currentProgress.revealedItemIds,
          STORY_FOREST_RUNE_STONE_INTERACTABLE_ID,
        ])
      : currentProgress.revealedItemIds,
    lastTriggeredEncounterId: encounter.encounterId,
    creaturesRemaining: Math.max(
      0,
      currentProgress.totalCreatureCount - defeatedCreatureCount,
    ),
    runeStoneRevealed,
  };
};

export const buildStoryForestEncounterBattle = ({
  encounterId,
  storyState = null,
} = {}) => {
  const encounter = getStoryForestEncounterById(encounterId);
  if (!encounter) return null;

  const { leaderKey, teammateKeys } = resolveStoryBattleTeam(storyState);
  const activeCompanion = resolveActiveStoryCompanion(storyState);

  return {
    launchMode: "direct_match",
    encounterId: encounter.encounterId,
    mode: "teams",
    storySide: {
      sideId: "red",
      leaderRole: buildStoryTeamBattleRole(leaderKey),
      teammateRoles: teammateKeys.map(buildStoryTeamBattleRole),
    },
    opponentSide: {
      sideId: "blue",
      opponentRoles: encounter.creatureIds.map((creatureId, index) => ({
        source: STORY_BATTLE_ROLE_SOURCE.STORY_CREATURE,
        characterId: creatureId,
        alias:
          encounter.creatureIds.length > 1
            ? `${creatureId}-${index + 1}`
            : creatureId,
      })),
    },
    roomConfig: {
      amountOfPlayers:
        1 +
        teammateKeys.length +
        Math.max(1, encounter.creatureIds.length),
      botFill: false,
      public: false,
      ranked: false,
      timedTurns: true,
      turnTime: 30,
      mode: "teams",
      teamCount: 2,
      teamOrder: ["red", "blue"],
    },
    completionPolicy: {
      defaultResultKey: "completed_unknown",
      onResults: {},
    },
    returnTarget: buildStoryForestEncounterReturnTarget(encounter.encounterId),
    resumePolicy: {
      allowResumeActiveMatch: true,
      restartIfRoomMissing: true,
    },
    storyCompanionAura: activeCompanion
      ? {
          companionId: activeCompanion.id,
        }
      : null,
  };
};
